import type { AppBootstrapOptions } from 'owa-app-module/lib/types/AppBootstrapOptions';
import { peopleHugPageTitle } from './bootstrap.locstring.json';
import loc from 'owa-localize';

import { polyfill } from './polyfills/polyfills';
polyfill(); // This has to come after optin otherwise the former explodes in IE

import debounce from 'lodash-es/debounce';
import * as React from 'react';
import configureStore from './store';

import { loadDataRequiedForDefaultView, personaListViewDisplayOrderChanged } from 'people/lib/hub';
import {
    App,
    addPublicFolderToFavoritesCompletedAction,
    updateOverlayPaneStatus,
} from 'people/lib/appchrome';
import getPeopleHeaderProps from 'people/lib/appchrome/components/headerWithSearchBox/getPeopleHeaderProps';

import {
    isDev,
    peopleIconsRegistration,
    Logger,
    setLogger,
    registerNotificationHandler,
    markBootstrapEvent,
    ThunkDispatch,
    FolderGroupType,
} from 'people/lib/shared';

import { getRouterOptions } from './routes';
import { getUserConfiguration } from 'owa-session-store';
import {
    initUserConfiguration,
    initializeLoki,
    userConfigurationReceivedAction,
    ContactFolder,
    isPublicFoldersEnabled,
    getAppState,
    widthBucketUpdated,
    updateLeftNavVisibility,
    updateTabletFlag,
} from 'people/lib/appstate';

import { withContext } from 'recompose';
import { registerTracerListener, TraceLevel } from 'owa-trace';
import { controlIconRegistration } from 'owa-control-icons';
import { mailIconsRegistration } from 'owa-mail-icons';
import { logUsage } from 'owa-analytics';
import { object } from 'prop-types';
import { getSessionId } from 'owa-config';
import type { SessionData } from 'owa-service/lib/types/SessionData';
import { setDateFormat, setTimeFormat, setLocalTimeZone } from 'owa-datetime-store';
import { OwaWorkload, Module } from 'owa-workloads';
import type { Dispatch } from 'redux';
import { orchestrator, ActionCreator, ActionMessage } from 'satcheljs';
import { addPublicFolderToFavoritesCompleted } from 'owa-publicfolder-picker/lib/actions/addPublicFolderToFavorites';
import type DisplayOrder from 'owa-people-types/lib/types/DisplayOrder';
import { hasQueryStringParameter } from 'owa-querystring';
import { lazySetupPeopleFunctionalBoot } from 'people/lib/people-post-boot';
import {
    initializeDynamicLayout,
    BrowserWidthBucket,
    onAvailableWidthBucketChanged,
} from 'owa-layout';
import type { BootStrategies } from 'owa-shared-start';
import type { SearchBoxContainerHandle } from 'owa-search/lib/types/SearchBoxContainerHandle';

// Promise that resolves after we have processed session data, for PLT Query that has dependency on UserConf.
let afterSessionDataCallback: () => void;
// tslint:disable-next-line:promise-must-complete
const afterSessionDataPromise = new Promise<void>(resolve => (afterSessionDataCallback = resolve));

export default function getPeopleBootstrapOptions(
    sessionPromise: Promise<SessionData | undefined>,
    strategies?: BootStrategies
): Promise<AppBootstrapOptions> {
    const initializeLpcModule = import(/*webpackChunkName: "InitializeLPC"*/ './initializeLpc');
    const uiFabricIconsModule = import('@fluentui/font-icons-mdl2');
    const logger = createLogger();
    setLogger(logger);

    addInitialBootstrapStartMarks(sessionPromise);

    configureErrorHandling();

    initializeDynamicLayout(false);

    // Test for ES2015 on evergreen browsers
    return configureStore(logger).then(store => {
        loadDataRequiedForDefaultView(store.dispatch, afterSessionDataPromise);

        return {
            initializeState: () => {
                const userConfig = getUserConfiguration();
                initUserConfiguration(store.dispatch, userConfig);
                initializeDateTimeOrchestrators(store.dispatch);
                initializeOptionOrchestrator(store.dispatch);
                initializeBrowserWidth(store.dispatch, store.getState);

                if (isPublicFoldersEnabled(getAppState(store.getState()))) {
                    initializePublicFolderPickerOrchestrator(store.dispatch);
                }

                initializeLoki(logger.log).then(() => {
                    initializeLpcModule
                        .then(module => {
                            (store.dispatch as ThunkDispatch)(module.initializeLivePersonaCard);
                            return module;
                        })
                        .then(module =>
                            (store.dispatch as ThunkDispatch)(module.initializeLivePersonaEditor)
                        );
                });
                uiFabricIconsModule.then(module =>
                    module.initializeIcons(undefined, { disableWarnings: true })
                );
                registerNotificationHandler();
                import(
                    /* webpackChunkName: "DisablePersonaCard" */ 'owa-persona/lib/actions/disablePersonaCard'
                ).then(module => module.default());
                (window as any).peopleHub = { sessionId: getSessionId };
                afterSessionDataCallback();
            },
            sessionPromise,
            routerOptions: getRouterOptions(store),
            getModuleName: () => loc(peopleHugPageTitle),
            iconFonts: [controlIconRegistration, mailIconsRegistration, peopleIconsRegistration],
            appModuleProps: {
                module: Module.People,
                renderModule: (searchBoxRef: React.RefObject<SearchBoxContainerHandle>) => {
                    markBootstrapEvent('AppComponentPrerender');
                    const AppWithContext = withContext({ logger: object.isRequired }, () => {
                        return { logger };
                    })(App);
                    return <AppWithContext store={store} searchBoxRef={searchBoxRef} />;
                },
                getHeaderInfo: getPeopleHeaderProps(store),
            },
            analyticsOptions: {
                ariaTenantTokens: [
                    '90c31e9f702c4564b120167247d3cd9f-d894a4e5-72f4-4221-ab00-94e1359dfb60-7137',
                ],
                qosDatapointNames: [
                    'ContactCleanup_FetchPersonaLinksSuggestion',
                    'ContactCleanup_SubmitPersonaLinks',
                    'ContactListsInCommandBar_addMembersToPrivateDL',
                    'ContactListsInCommandBar_removeMembersFromPrivateDL',
                    'ContactManagement_CreatePersona',
                    'ContactManagement_DeletePersona',
                    'ContactManagement_DeletePersonas',
                    'ContactManagement_FindPeople',
                    'ContactManagement_FindPeopleInSparseFolder',
                    'ContactManagement_GetContactFolders',
                    'ContactManagement_GetFolder',
                    'ContactManagement_GetPersona',
                    'ContactManagement_GetPersonaByEmail',
                    'ContactManagement_GetPersonaNotes',
                    'ContactManagement_GetSuggestedPersonas',
                    'ContactManagement_UpdatePersona',
                    'ExportContacts_ExportContactList',
                    'Favorite_AddFavorite',
                    'Favorite_AddRoamingFavorite',
                    'Favorite_EditRoamingFavorite',
                    'Favorite_RemoveFavorite',
                    'Favorite_RemoveRoamingFavorite',
                    'FolderManagement_CreateFolder',
                    'FolderManagement_DeleteFolder',
                    'FolderManagement_EmptyFolder',
                    'FolderManagement_HardDeleteFolder',
                    'FolderManagement_MoveFolder',
                    'FolderManagement_RemoveFavoritePublicFolder',
                    'FolderManagement_RenameFolder',
                    'HardDelete_HardDelete',
                    'ImportContacts_ImportContactList',
                    'Linking_LinkPersonas',
                    'Linking_UnlinkContact',
                    'LivePersonaCard_InitializeLPC',
                    'LivePersonaCard_InvalidateCachedContacts',
                    'MoveContacts_MoveContacts',
                    'PersistData_PersistPeopleHubOptions',
                    'PickerSearch_GetFilteredPeopleTypeFromContacts',
                    'PickerSearch_GetFilteredPeopleTypeFromFederatedSources',
                    'PickerSearch_GetFilteredPeopleTypeFromSuggestions',
                    'PrivateDistributionList_CreatePrivateDL',
                    'PrivateDistributionList_DistributionListsInfo',
                    'PrivateDistributionList_DistributionListsMembers',
                    'PrivateDistributionList_DistributionListsNotes',
                    'PrivateDistributionList_GetEmailToPDLsMapping',
                    'PrivateDistributionList_GetFilteredEmailsAndPDLsFromFolders',
                    'PrivateDistributionList_GetFilteredEmailsAndPDLsFromSuggestions',
                    'PrivateDistributionList_UpdatePrivateDL',
                    'RestoreDeletedContacts_FindRecoverableItems',
                    'RestoreDeletedContacts_GetDeletedItemsFolders',
                    'RestoreDeletedContacts_RestoreContacts',
                    'Search_GetSearchedPersonasContacts',
                    'Search_GetSearchedUnifiedGroups',
                    'Search_SearchFederatedSources',
                    'Search_SearchSuggestedPersonas',
                    'SoftDelete_SoftDelete',
                    'UnifiedGroups_CreateUnifiedGroupPrivacyTask',
                    'UnifiedGroups_GetUnifiedGroupDetails',
                    'UnifiedGroups_GetUnifiedGroupMembershipRequests',
                    'UnifiedGroups_GetUnifiedGroupPrivacyTask',
                    'UnifiedGroups_GetUnifiedGroupsSettings',
                    'UnifiedGroups_GetUserUnifiedGroups',
                    'UnifiedGroups_JoinPrivateUnifiedGroup',
                    'UnifiedGroups_RenewUnifiedGroup',
                    'UnifiedGroups_RestoreUnifiedGroup',
                    'UnifiedGroups_SetUnifiedGroupFavoriteState',
                    'UnifiedGroups_SetUnifiedGroupMembershipState',
                    'UnifiedGroups_SetUnifiedGroupSubscribeState',
                ],
            },
            workload: OwaWorkload.People,
            postLazyAction: lazySetupPeopleFunctionalBoot,
            postLazyArgs: [store],
            strategies,
        };
    });
}

const addInitialBootstrapStartMarks = (sessionPromise: Promise<SessionData>): void => {
    markBootstrapEvent('Start');
    sessionPromise.then(() => markBootstrapEvent('SessionReturned'));
};

function configureErrorHandling(): void {
    // Register a handler for all trace errors.
    // We want these errors to be shown as alert() in devbox
    registerTracerListener((message, traceLevel, _error) => {
        if (isDev() && traceLevel === TraceLevel.Error) {
            //alert(message);
        }
    });

    // In dev mode override console.error and show annoying alert
    if (isDev() && window.console) {
        const consoleError = console.error;

        console.error = (message?: any, ...optionalParams: any[]) => {
            //alert(`Fix console error: ${message}`);
            consoleError(message, optionalParams);
        };
    }
}

function createLogger(): Logger {
    const isAriaConsoleLogEnabled = hasQueryStringParameter('logtoconsole');
    let log: (name: string, properties: { [key: string]: string }) => void = (name, properties) =>
        setTimeout(() => logUsage(name, properties));

    if (isAriaConsoleLogEnabled) {
        const originLog = log;
        log = (name, properties) => {
            // tslint:disable-next-line:no-console
            console.log('-----AriaLogger-----', name, properties);
            originLog(name, properties);
        };
    }

    return {
        log: log,
    };
}

function initializeDateTimeOrchestrators(dispatch: Dispatch): void {
    const forceUpdateDebounced = debounce(() => {
        const userConfig = getUserConfiguration();
        dispatch(userConfigurationReceivedAction({ userConfig }));
    }, 0);

    // setDateFormat, setTimeFormat, setLocalTimeZone formats belong to a different module,
    // so we'll need to attach to them separately to watch for user config update
    [setDateFormat, setTimeFormat, setLocalTimeZone].forEach(
        (action: ActionCreator<ActionMessage>) => {
            orchestrator(action, () => {
                forceUpdateDebounced();
            });
        }
    );
}

function initializePublicFolderPickerOrchestrator(dispatch: Dispatch): void {
    orchestrator(addPublicFolderToFavoritesCompleted, actionMessage => {
        const folder: ContactFolder = {
            id: actionMessage.folderId,
            displayName: actionMessage.folderName,
            children: null,
            isReadOnly: true,
            group: FolderGroupType.publicFolders,
            publicFolderInfo: {
                folderSmtpAddress: null,
                effectiveRights: null,
            },
        };

        dispatch(addPublicFolderToFavoritesCompletedAction({ folder }));
    });
}

function initializeOptionOrchestrator(dispatch: Dispatch): void {
    import(
        /* webpackChunkName: "DisplayByOrderUpdated" */ 'owa-people-displayby-option/lib/actions/displayByOrderUpdated'
    ).then(module => {
        orchestrator(module.displayByOrderUpdated, actionMessage => {
            const displayBy: DisplayOrder = actionMessage.displayBy;
            personaListViewDisplayOrderChanged(dispatch)(displayBy);
        });
    });
}

function initializeBrowserWidth(dispatch: Dispatch, getState: () => {}): void {
    orchestrator(onAvailableWidthBucketChanged, actionMessage => {
        const browserWidthBucket = actionMessage.availableWidthBucket;
        const appState = getAppState(getState());
        const isTablet = appState.view.layoutState.isTablet;

        switch (browserWidthBucket) {
            case BrowserWidthBucket.From418_Below:
            case BrowserWidthBucket.From419_To499:
            case BrowserWidthBucket.From500_To541:
            case BrowserWidthBucket.From542_To554:
            case BrowserWidthBucket.From555_To579:
            case BrowserWidthBucket.From580_To685:
            case BrowserWidthBucket.From686_To691:
            case BrowserWidthBucket.From692_To768:
            case BrowserWidthBucket.From769_To865:
            case BrowserWidthBucket.From866_To899:
            case BrowserWidthBucket.From900_To918:
            case BrowserWidthBucket.From919_To999:
            case BrowserWidthBucket.From1000_To1079:
                if (!isTablet) {
                    updateOverlayPaneStatus(dispatch)(true);
                    updateLeftNavVisibility(dispatch)(false);
                    updateTabletFlag(dispatch)(true);
                }
                break;
            case BrowserWidthBucket.From1080_To1199:
            case BrowserWidthBucket.From1200_To1238:
            case BrowserWidthBucket.From1239_To1300:
            case BrowserWidthBucket.From1301_To1365:
            case BrowserWidthBucket.From1366_To1399:
            case BrowserWidthBucket.From1400_To1599:
            case BrowserWidthBucket.From1600_Above:
            default:
                if (isTablet) {
                    updateOverlayPaneStatus(dispatch)(false); // ShowoverlayPane Handler when user resize application from tablet viewport size to desktop size, setting up value as false as overlaypane won't be shown in this mode
                    updateLeftNavVisibility(dispatch)(true);
                    updateTabletFlag(dispatch)(false);
                }
                break;
        }

        widthBucketUpdated(dispatch)(browserWidthBucket);
    });
}
